import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BOOKING_URL } from "./shared";

const links = [
  ["The Problem", "problem"],
  ["Framework", "framework"],
  ["Seven Days", "seven-days"],
  ["Proof", "proof"],
  ["Investment", "investment"],
  ["FAQ", "faq"],
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const LogoMark = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" fill="none" className={className}>
    <circle cx="8" cy="30" r="3.4" fill="#0E5F46" />
    <circle cx="20" cy="9" r="3.4" fill="#0B1B2B" />
    <circle cx="33" cy="24" r="3.4" fill="#E8833A" />
    <circle cx="21" cy="31" r="2.4" fill="#3B7BC8" />
    <path d="M8 30 20 9M20 9l13 15M8 30l13 1m0 0 12-7" stroke="#0B1B2B" strokeOpacity="0.45" strokeWidth="1.4" />
  </svg>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_1px_0_rgba(11,27,43,0.08)]" : "bg-transparent"}`}
      data-testid="main-nav"
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 h-[74px] flex items-center justify-between gap-3">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 min-w-0" data-testid="nav-logo">
          <LogoMark />
          <span className="text-left leading-none min-w-0">
            <span className="block font-display font-extrabold tracking-[0.16em] text-[19px] text-[#0B1B2B]">SENIORS</span>
            <span className="block text-[12px] tracking-[0.2em] uppercase text-[#0B1B2B]/70 mt-[3px] font-bold whitespace-nowrap">Implementation Consulting</span>
          </span>
        </button>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map(([label, id]) => (
            <button
              type="button"
              key={id}
              onClick={() => scrollTo(id)}
              data-testid={`nav-link-${id}`}
              className="nav-link text-[13.5px] font-semibold text-[#0B1B2B]/75 hover:text-[#0B1B2B] transition-colors"
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="inline-flex items-center h-[38px] sm:h-[42px] px-4 sm:px-6 rounded-full bg-[#0E5F46] text-white text-[12px] sm:text-[13.5px] font-bold tracking-wide hover:bg-[#0B4D39] transition-all duration-300 hover:shadow-[0_8px_24px_rgba(14,95,70,0.35)] whitespace-nowrap"
          >
            <span className="sm:hidden">Book a Call</span>
            <span className="hidden sm:inline">Book a Discovery Call</span>
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation menu"
            className="lg:hidden w-[38px] h-[38px] rounded-full bg-white border border-[#0B1B2B]/12 flex items-center justify-center text-[#0B1B2B]"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#0B1B2B]/8 shadow-[0_20px_40px_rgba(11,27,43,0.12)]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col">
              {links.map(([label, id]) => (
                <button
                  type="button"
                  key={id}
                  onClick={() => { setMenuOpen(false); scrollTo(id); }}
                  data-testid={`mobile-nav-link-${id}`}
                  className="text-left py-3.5 text-[15px] font-semibold text-[#0B1B2B] border-b border-[#0B1B2B]/6 last:border-0"
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
